/**
 * Pure helpers for Subscribe ATC, gift matching, and $100 bottle opt-out.
 * Loaded by the theme and required by tests/joi-cart-gifts-logic.test.js.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  root.JoiCartGiftsLogic = api;
})(typeof window !== 'undefined' ? window : typeof globalThis !== 'undefined' ? globalThis : this, function () {
  function isGiftItem(item) {
    return !!(item && item.properties && item.properties._Free_Gift);
  }

  function isSub(item) {
    return !!(item && item.selling_plan_allocation);
  }

  function linePlanId(item) {
    if (!item || !item.selling_plan_allocation) return '';
    var sp = item.selling_plan_allocation.selling_plan;
    if (sp && sp.id != null) return String(sp.id);
    if (item.selling_plan_allocation.selling_plan_id != null) {
      return String(item.selling_plan_allocation.selling_plan_id);
    }
    return '';
  }

  /**
   * Grouping signature for de-duping parent lines. Subscriptions are keyed by
   * their delivery-frequency NAME (e.g. "deliver every 4 weeks"), not the raw
   * selling_plan id: a product can expose two plans that both render as the
   * same frequency, and to the customer two such lines are the same thing and
   * must collapse. Falls back to the plan id when no name is present. One-time
   * lines share the 'otp' signature (unchanged from keying on an empty plan id).
   */
  function planSignature(item) {
    var alloc = item && item.selling_plan_allocation;
    var sp = alloc && alloc.selling_plan;
    if (!sp) return 'otp';
    if (sp.name) return 'name:' + String(sp.name).toLowerCase().replace(/\s+/g, ' ').trim();
    var id = linePlanId(item);
    return id ? 'id:' + id : 'otp';
  }

  function itemCadence(item) {
    var alloc = item && item.selling_plan_allocation;
    var sp = alloc && alloc.selling_plan;
    var cad = cadenceToken(sp && sp.name);
    if (cad) return cad;
    var props = item && item.properties;
    if (props) {
      cad = cadenceToken(
        String(props.shipping_interval_frequency || props.Shipping_Interval_Frequency || '') +
          ' ' +
          String(props.shipping_interval_unit_type || props.Shipping_Interval_Unit_Type || '')
      );
      if (cad) return cad;
    }
    return '';
  }

  /**
   * Full identity for merging duplicate parent lines: variant + cadence + unit
   * price. Cadence ("4:week") folds "Deliver Every 4 Weeks" with "Every 4 Weeks"
   * — Rebuy add.js often uses a different plan id/name than the PDP line.
   * The price term keeps retention/Stay plans from folding into a cheaper line.
   */
  function freqMergeKey(item) {
    var cad = itemCadence(item);
    if (cad) return 'cadence:' + cad;
    return planSignature(item);
  }

  function lineMergeSignature(item) {
    var unit = item && item.final_price != null ? item.final_price : (item && item.price) || 0;
    return String((item && item.variant_id) || '') + '::' + freqMergeKey(item) + '::' + unit;
  }

  /**
   * Automatic/BXGY discount identity on a parent line. Shopify splits a
   * Subscribe 4-pack when qty goes 1→2: one line keeps "4-Pack - Free Trail
   * pack", the extra unit has none. Those siblings share lineMergeSignature
   * (so +/- still treats them as one family) but MUST NOT merge — collapsing
   * them fights the discount engine and the cart snaps back to qty 1.
   */
  function discountAllocKey(item) {
    var titles = [];
    var seen = {};
    function addTitle(raw) {
      var t = String(raw || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
      if (!t || seen[t]) return;
      seen[t] = true;
      titles.push(t);
    }
    var allocs = (item && item.line_level_discount_allocations) || [];
    var i;
    for (i = 0; i < allocs.length; i++) {
      var app = allocs[i] && allocs[i].discount_application;
      addTitle(app && (app.title || app.description));
    }
    var discs = (item && item.discounts) || [];
    for (i = 0; i < discs.length; i++) addTitle(discs[i] && discs[i].title);
    titles.sort();
    return titles.length ? titles.join('|') : 'none';
  }

  /** Merge grouping: family identity plus discount allocation. */
  function lineDedupeSignature(item) {
    return lineMergeSignature(item) + '::' + discountAllocKey(item);
  }

  function familyItems(items, item, giftFn) {
    giftFn = giftFn || isGiftItem;
    if (!item) return [];
    var sig = lineMergeSignature(item);
    return (items || []).filter(function (it) {
      return it && !giftFn(it) && lineMergeSignature(it) === sig;
    });
  }

  function familyQuantity(items, item, giftFn) {
    var total = 0;
    familyItems(items, item, giftFn).forEach(function (it) {
      total += it.quantity || 0;
    });
    return total;
  }

  /**
   * One change.js write to move a family to `familyTarget`. Grows/shrinks the
   * non-discounted extra line when one exists so Shopify does not re-split
   * the BXGY-entitled parent. Null if already at target.
   */
  function familyQtyWrite(lines, familyTarget, keyFn) {
    keyFn =
      keyFn ||
      function (l) {
        return l && l.key != null ? String(l.key) : '';
      };
    var list = (lines || []).filter(function (l) {
      return l && keyFn(l);
    });
    if (!list.length) return null;
    var current = 0;
    list.forEach(function (l) {
      current += l.quantity || 0;
    });
    var target = Math.max(0, Number(familyTarget) || 0);
    if (current === target) return null;
    var delta = target - current;
    var extras = list.filter(function (l) {
      return discountAllocKey(l) === 'none';
    });
    var pool = extras.length ? extras : list;
    var line = pool[0];
    var i;
    if (delta < 0) {
      for (i = 1; i < pool.length; i++) {
        if ((pool[i].quantity || 0) > (line.quantity || 0)) line = pool[i];
      }
    }
    return { key: keyFn(line), quantity: Math.max(0, (line.quantity || 0) + delta) };
  }

  function trialUnitsFor(item) {
    if (!item || isGiftItem(item) || !isSub(item)) return 0;
    var hay = ((item.handle || '') + ' ' + (item.product_title || '') + ' ' + (item.title || '')).toLowerCase();
    if (/3-pack|3pk|three-pack/.test(hay)) return 0;
    if (/\bbulk\b|pail/.test(hay) || /-base-1(?:-|$)/.test(String(item.handle || '').toLowerCase())) return 2;
    if (/6-pack|6pk|six-pack/.test(hay)) return 2;
    if (/\bfamily\b/.test(hay)) return 2;
    if (/4-pack|4pk|four-pack/.test(hay)) return 1;
    return 0;
  }

  function expectedTrial(cart) {
    var total = 0;
    ((cart && cart.items) || []).forEach(function (item) {
      var per = trialUnitsFor(item);
      if (per) total += per * (item.quantity || 0);
    });
    return total;
  }

  function eligibleCents(cart) {
    var total = 0;
    ((cart && cart.items) || []).forEach(function (item) {
      if (isGiftItem(item)) return;
      total += item.final_line_price || item.line_price || 0;
    });
    return total;
  }

  function findExistingSubscribeLine(items, variantId, planId, planName) {
    var varId = String(variantId || '');
    var plan = String(planId || '');
    // A Subscribe add always carries a real selling plan. With an empty plan
    // this would match the first planless line (a one-time purchase) and then
    // change.js would bump that OTP line instead of adding the subscription.
    if (!plan) return null;
    var list = items || [];
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item || !item.key) continue;
      if (String(item.variant_id) !== varId) continue;
      if (isGiftItem(item)) continue;
      if (linePlanId(item) === plan) return item;
    }
    // Exact selling-plan id only. A tempting "same frequency NAME" fallback
    // (two plan groups can both label a plan "Deliver Every 4 Weeks") was
    // removed: bumping a same-name line on a DIFFERENT plan id inherits that
    // line's price, so a customer picking a $30 plan could be charged the
    // existing $40 plan's rate. On an id miss we add a new line; the price-aware
    // merge (mergeDuplicateParents / lineMergeSignature) then folds it only when
    // variant + frequency + unit price all match — never across a price gap.
    // planName is retained in the signature for backwards-compatible callers.
    return null;
  }

  function shouldHaveBottle(eligible, thresholdCents) {
    return eligible > (thresholdCents || 10000);
  }

  /**
   * Manual bottle remove sticks until the cart drops back to $100 or under.
   * Crossing the threshold again after that re-adds the bottle.
   */
  function shouldAddBottle(shouldHave, hasGift, optedOut, pendingRemove) {
    if (!shouldHave || hasGift) return false;
    if (optedOut || pendingRemove) return false;
    return true;
  }

  function shouldClearBottleOptOut(shouldHave) {
    return !shouldHave;
  }

  // Shopify line-item keys look like "<variant_id>:<hex>" (e.g. 4032:e3f1...).
  // Rebuy sometimes puts unrelated data-key attributes on inner nodes, so we
  // use this to prefer a value that actually looks like a line key.
  function looksLikeLineKey(v) {
    // variant_id:hash, plus optional extra Shopify key segments (properties hash)
    return /^\d+:[0-9a-z]+(?::[0-9a-z]+)*$/i.test(String(v || ''));
  }

  function normalizePlanName(name) {
    return String(name || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function cadenceToken(raw) {
    var n = normalizePlanName(raw);
    var m = n.match(/(\d+)\s*-?\s*(week|day|month)/);
    return m ? m[1] + ':' + m[2] : '';
  }

  function planNameMatchesItem(item, raw) {
    var want = cadenceToken(raw);
    if (want) {
      var sp = item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      return cadenceToken(sp && sp.name) === want;
    }
    var n = normalizePlanName(raw);
    if (!n) return false;
    return planSignature(item) === 'name:' + n;
  }

  function rowLineKey(attrs) {
    if (!attrs) return '';
    var order = [
      'data-key',
      'data-line-key',
      'data-line-item-key',
      'data-item-key',
      'data-cart-item-key',
      'key'
    ];
    var firstNonEmpty = '';
    for (var i = 0; i < order.length; i++) {
      var v = attrs[order[i]];
      if (!v) continue;
      v = String(v);
      if (looksLikeLineKey(v)) return v; // prefer a real Shopify key
      if (!firstNonEmpty) firstNonEmpty = v;
    }
    return firstNonEmpty;
  }

  function isConfiguredGiftHandle(handle, trialHandle, bottleHandle) {
    var h = String(handle || '').toLowerCase();
    if (!h) return false;
    if (trialHandle && h === String(trialHandle).toLowerCase()) return true;
    if (bottleHandle && h === String(bottleHandle).toLowerCase()) return true;
    return false;
  }

  /**
   * Rebuy's flyout row class list starts with `product-{handle}` (see
   * `'product-' + item.handle` in the Smart Cart template). Default rows have
   * no data-key and the title <a> may have a null href when the item isn't
   * clickable — the class is then the only handle on the DOM.
   */
  function handleFromClassName(className) {
    var m = String(className || '').match(/(?:^|\s)product-([a-z0-9][a-z0-9-]*)(?:\s|$)/i);
    return m ? m[1].toLowerCase() : '';
  }

  function normalizeTitle(s) {
    return String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /**
   * Pick a unique paid parent from a candidate list (variant or handle match).
   * Same rules as the unkeyed path: unique line, else qty, else interchangeable
   * merge-signature duplicates. Ambiguous genuine differences return null.
   */
  function pickUniqueParent(candidates, shownQty) {
    var matches = candidates || [];
    if (matches.length === 1) return matches[0];
    if (shownQty != null) {
      var byQty = matches.filter(function (item) {
        return (item.quantity || 0) === shownQty;
      });
      if (byQty.length === 1) return byQty[0];
    }
    if (matches.length > 1) {
      var sig = lineMergeSignature(matches[0]);
      var interchangeable = matches.every(function (item) {
        return lineMergeSignature(item) === sig;
      });
      if (interchangeable) return matches[0];
    }
    return null;
  }

  /**
   * OTP vs Subscribe of the same SKU, no cadence on the row: the OTP row
   * has no frequency text, so planName is empty. Prefer the single
   * planless line instead of returning null (which used to eat the click
   * after the qty-gate already blocked Rebuy).
   */
  function pickOtpIfUnambiguous(candidates, shownQty, planName) {
    if (planName || !candidates || candidates.length < 2) return null;
    var otp = candidates.filter(function (item) {
      return planSignature(item) === 'otp';
    });
    if (otp.length !== 1) return null;
    return pickUniqueParent(otp, shownQty);
  }

  function isSameQtyTarget(a, b) {
    if (!a || !b) return false;
    if (a.row && b.row && a.row === b.row) return true;
    if (a.key && b.key && String(a.key) === String(b.key)) return true;
    return false;
  }

  /**
   * Fold a qty intent into a pending queue. Same row/key accumulates
   * delta; a different row becomes a new job so B's +/- never lands on A.
   */
  function coalesceQtyJob(queue, info) {
    var q = queue ? queue.slice() : [];
    var job = {
      row: info && info.row,
      key: (info && info.key) || '',
      delta: (info && info.delta) || 0
    };
    var last = q.length ? q[q.length - 1] : null;
    if (last && isSameQtyTarget(last, job)) {
      last.delta += job.delta;
      if (job.key) last.key = job.key;
      return q;
    }
    q.push(job);
    return q;
  }

  /**
   * After an exact key miss (or no key), rematch a paid pack row by variant
   * then handle + frequency. Gift-SKU handles never rematch — paid trial/bottle
   * share those products and intercepting would bump the wrong line.
   */
  function matchParentByIdentity(opts, items, giftFn) {
    var shownQty = opts && opts.shownQty;
    var variantId = opts && opts.variantId != null && String(opts.variantId) !== '' ? String(opts.variantId) : '';
    if (variantId) {
      var byVar = items.filter(function (item) {
        return item && !giftFn(item) && String(item.variant_id) === variantId;
      });
      var pickedVar = pickUniqueParent(byVar, shownQty);
      if (pickedVar) return pickedVar;
      if (opts && opts.planName && byVar.length > 1) {
        var byVarPlan = byVar.filter(function (item) {
          return planNameMatchesItem(item, opts.planName);
        });
        pickedVar = pickUniqueParent(byVarPlan, shownQty);
        if (pickedVar) return pickedVar;
      }
      var otpVar = pickOtpIfUnambiguous(byVar, shownQty, opts && opts.planName);
      if (otpVar) return otpVar;
      if (byVar.length > 1) return null;
    }
    var handle = String((opts && opts.handle) || '').toLowerCase();
    if (isConfiguredGiftHandle(handle, opts && opts.trialHandle, opts && opts.bottleHandle)) {
      return null;
    }
    var matches = items.filter(function (item) {
      return item && !giftFn(item) && String(item.handle || '').toLowerCase() === handle;
    });
    if (opts && opts.planName && matches.length > 1) {
      var byPlan = matches.filter(function (item) {
        return planNameMatchesItem(item, opts.planName);
      });
      var pickedPlan = pickUniqueParent(byPlan, shownQty);
      if (pickedPlan) return pickedPlan;
    }
    var otpHandle = pickOtpIfUnambiguous(matches, shownQty, opts && opts.planName);
    if (otpHandle) return otpHandle;
    var pickedHandle = pickUniqueParent(matches, shownQty);
    if (pickedHandle) return pickedHandle;

    var title = normalizeTitle(opts && opts.title);
    if (title) {
      var byTitle = items.filter(function (item) {
        if (!item || giftFn(item)) return false;
        return normalizeTitle(item.product_title || item.title) === title;
      });
      var pickedTitle = pickUniqueParent(byTitle, shownQty);
      if (pickedTitle) return pickedTitle;
    }
    return null;
  }

  /**
   * Resolve a Rebuy qty-widget row to a paid parent line.
   * Live key wins. A key that is no longer in the cart (Stay/discount rotation,
   * merge) used to return null and hand the click to Rebuy add.js — that is
   * what flashed a second line. Stale keys now rematch like an unkeyed row.
   * Unkeyed gift-SKU rows still never match by handle.
   */
  function parentItemForQtyRow(opts) {
    var items = (opts && opts.items) || [];
    var giftFn = (opts && opts.isGiftItem) || isGiftItem;
    var key = rowLineKey(opts && opts.rowAttrs);
    if (key) {
      for (var i = 0; i < items.length; i++) {
        var keyed = items[i];
        if (!keyed || String(keyed.key) !== key) continue;
        return giftFn(keyed) ? null : keyed;
      }
    }
    return matchParentByIdentity(opts, items, giftFn);
  }

  /**
   * Gift UI only if this row maps to a cart line with _Free_Gift.
   * Never match by product handle (paid trial/bottle SKUs share handles).
   * Key-only: Rebuy row order does not track /cart.js order (gifts float,
   * flyout injects extra nodes), so an index fallback can badge a paid pack
   * and hide its +/-. No key -> no badge, rather than the wrong badge.
   */
  function giftMetaForRow(rowAttrs, items /* rowIndex unused: index is unsafe */) {
    var list = items || [];
    var key = rowLineKey(rowAttrs);
    if (!key) return null;
    for (var i = 0; i < list.length; i++) {
      var keyed = list[i];
      if (!keyed || String(keyed.key) !== key) continue;
      if (!isGiftItem(keyed)) return null;
      return { label: 'Free Gift', quantity: keyed.quantity || 1 };
    }
    return null;
  }

  /**
   * Fallback for Rebuy rows that render without a line key. Resolve by the
   * variant id taken from the row's own product link — NOT by DOM index or
   * payload position, which break when Rebuy floats gifts or injects extra
   * rows. Gifts use dedicated $0 SKUs, so a variant maps to exactly one line;
   * we only badge when that single line carries _Free_Gift. If the variant is
   * on two lines (ambiguous) or none, return null so no badge is applied —
   * a miss is acceptable, a wrong badge on a paid line is not.
   */
  function giftMetaByVariant(variantId, items) {
    var vid = String(variantId || '');
    if (!vid) return null;
    var list = items || [];
    var found = null;
    for (var i = 0; i < list.length; i++) {
      if (!list[i] || String(list[i].variant_id) !== vid) continue;
      if (found) return null; // variant on more than one line: ambiguous
      found = list[i];
    }
    if (!found || !isGiftItem(found)) return null;
    return { label: 'Free Gift', quantity: found.quantity || 1 };
  }

  function scheduleWaitMs(now, ignoreUntil, minDebounce) {
    var min = minDebounce == null ? 400 : minDebounce;
    return Math.max(min, (ignoreUntil || 0) - now);
  }

  function itemByLineKey(cart, key) {
    if (!cart || !key) return null;
    var items = cart.items || [];
    var i;
    for (i = 0; i < items.length; i++) {
      if (items[i] && String(items[i].key) === String(key)) return items[i];
    }
    return null;
  }

  /** Absolute qty after a +/- click. Never negative. */
  function qtyTargetAfterDelta(current, delta) {
    return Math.max(0, (Number(current) || 0) + (Number(delta) || 0));
  }

  /**
   * Gift/merge getCart that started before a +/- must not annotate or
   * fetchCart — that stale cart is what snaps the flyout back to the old qty.
   */
  function shouldDiscardGiftSync(runGen, liveGen, qtyInFlight) {
    return runGen !== liveGen || !!qtyInFlight;
  }

  function qtyJobCanUseCachedCart(cart, key) {
    return !!itemByLineKey(cart, key);
  }

  /** Gift/merge must not run while a qty write or queued +/- is in flight. */
  function giftSyncBlockedByQty(qtyBusy, pendingCount) {
    return !!(qtyBusy || pendingCount > 0);
  }

  /** After a +/- burst, gift sync uses this short wait instead of ignoreUntil. */
  function qtyGiftSyncWaitMs(now, qtyBurstUntil) {
    if (qtyBurstUntil && now < qtyBurstUntil) return 150;
    return 0;
  }

  /**
   * Writes for one duplicate-signature group. `total` is the intended qty
   * captured from the FIRST cart (not a re-sum of the current lines — re-summing
   * after set-succeeded + zero-failed inflates every pass). Survivor is a line
   * already at `total` if one exists, else the first line. Survivor is never
   * zeroed. Order: set survivor, then zero every other line.
   */
  function mergeCollapseWrites(lines, total, keyFn) {
    keyFn = keyFn || function (l) {
      return l && l.key != null ? String(l.key) : '';
    };
    var list = lines || [];
    if (list.length < 2) return [];
    var survivor = null;
    var i;
    for (i = 0; i < list.length; i++) {
      if ((list[i].quantity || 0) === total) {
        survivor = list[i];
        break;
      }
    }
    if (!survivor) survivor = list[0];
    var survivorKey = keyFn(survivor);
    if (!survivorKey) return [];
    var writes = [];
    if ((survivor.quantity || 0) !== total) {
      writes.push({ key: survivorKey, quantity: total });
    }
    for (i = 0; i < list.length; i++) {
      var k = keyFn(list[i]);
      if (!k || k === survivorKey) continue;
      if ((list[i].quantity || 0) !== 0) writes.push({ key: k, quantity: 0 });
    }
    return writes;
  }

  /** @deprecated pair helper — production merge uses mergeCollapseWrites */
  function mergeFoldWrites(keep, extra) {
    return mergeCollapseWrites([keep, extra], (keep && keep.quantity || 0) + (extra && extra.quantity || 0));
  }

  /**
   * Detect Rebuy Smart Cart +/- on a flyout row. Shared by the head qty-gate
   * (must run before Rebuy's ScriptTags) and the body fallback intercept.
   */
  function rebuyQtyIntent(target) {
    if (!target || typeof target.closest !== 'function') return null;
    var row = target.closest('.rebuy-cart__flyout-item, .rebuy-cart__item, .line-item');
    if (!row) return null;
    if (row.classList && row.classList.contains('is-free-gift')) return null;
    if (
      target.closest(
        '.rebuy-cart__flyout-item-remove, .line-item-remove, .remove-line, [aria-label*="emove"]'
      )
    ) {
      return null;
    }
    var rowCls = (row.getAttribute && row.getAttribute('class')) || '';
    if (/(?:^|\s)line-item(?:\s|$)/.test(rowCls)) {
      var upd = target.closest('a.cart-update, .cart-update');
      if (!upd) return null;
      var updCls = ((upd.getAttribute && upd.getAttribute('class')) || '').toLowerCase();
      if (updCls.indexOf('line-item-remove') !== -1) return null;
      return { row: row, delta: /\bplus\b/.test(updCls) ? 1 : -1 };
    }
    try {
      if (target.closest('a[href*="/products/"], a[href*="edit" i], [aria-label*="edit" i]')) {
        return null;
      }
    } catch (e) {
      if (target.closest('a[href*="/products/"]')) return null;
    }

    var widget = target.closest('.rebuy-cart__flyout-item-quantity-widget')
      || target.closest('.rebuy-cart__flyout-item-quantity')
      || target.closest('[class*="rebuy"][class*="quantity"]');
    var qtyCtrlSel =
      'button, [role="button"], a, .rebuy-cart__flyout-item-quantity-widget-button, [data-action="increase"], [data-action="decrease"]';
    var btn = target.closest(qtyCtrlSel);
    // The plus/minus <button> class contains both "rebuy" and "quantity", so
    // the loose attribute selector above can resolve to the button itself.
    // Walk up so first/last-child heuristics still see the widget.
    if (widget && btn && widget === btn && btn.parentElement) {
      widget = btn.parentElement.closest
        ? btn.parentElement.closest(
            '.rebuy-cart__flyout-item-quantity-widget, .rebuy-cart__flyout-item-quantity'
          ) || btn.parentElement
        : btn.parentElement;
    }
    var aria = ((btn && btn.getAttribute && btn.getAttribute('aria-label')) || '').toLowerCase();
    var action = '';
    if (btn && btn.getAttribute) {
      action = String(btn.getAttribute('data-action') || btn.getAttribute('data-qty-action') || '').toLowerCase();
    }
    // Read class via getAttribute: on an inline <svg> icon target, .className is
    // an SVGAnimatedString object, not a string, so `svg.className` stringifies
    // to '[object SVGAnimatedString]' and the real icon-plus/icon-minus classes
    // are lost — breaking +/- detection and forcing the fragile positional path.
    function clsOf(el) {
      if (!el) return '';
      if (el.getAttribute) {
        var a = el.getAttribute('class');
        if (a != null) return a;
      }
      return typeof el.className === 'string' ? el.className : '';
    }
    var cls = (clsOf(btn) + ' ' + clsOf(target)).toLowerCase();
    var incRe = /\b(increase|increment|plus)\b/;
    var decRe = /\b(decrease|decrement|minus)\b/;
    var inc = incRe.test(aria) || incRe.test(cls) || incRe.test(action);
    var dec = decRe.test(aria) || decRe.test(cls) || decRe.test(action);

    if (!inc && !dec && widget) {
      var label = widget.querySelector
        ? widget.querySelector('.rebuy-cart__flyout-item-quantity-widget-label, input')
        : null;
      if (label && (target === label || (label.contains && label.contains(target)))) {
        return null;
      }
    }

    if (!inc && !dec && (btn || widget)) {
      var node = target;
      for (var i = 0; i < 4 && node && node !== row; i++) {
        var t = String(node.textContent || '').replace(/\s+/g, '');
        if (t === '+') {
          inc = true;
          break;
        }
        if (t === '-' || t === '−' || t === '–') {
          dec = true;
          break;
        }
        node = node.parentElement;
      }
    }

    if (!inc && !dec && widget) {
      var buttons = widget.querySelectorAll ? widget.querySelectorAll(qtyCtrlSel) : [];
      if (buttons.length >= 2) {
        var hit = btn;
        if (!hit) {
          var b;
          for (b = 0; b < buttons.length; b++) {
            if (buttons[b] === target || (buttons[b].contains && buttons[b].contains(target))) {
              hit = buttons[b];
              break;
            }
          }
        }
        if (hit && (hit === buttons[0] || (buttons[0].contains && buttons[0].contains(hit)))) {
          dec = true;
        } else if (
          hit &&
          (hit === buttons[buttons.length - 1] ||
            (buttons[buttons.length - 1].contains && buttons[buttons.length - 1].contains(hit)))
        ) {
          inc = true;
        }
      }
    }

    if (!inc && !dec) return null;
    return { row: row, delta: inc ? 1 : -1 };
  }

  /**
   * Prefer class/aria detection; if the click landed on the quantity widget
   * chrome (CSS plus icon, empty div) fall back to left = minus, right = plus.
   */
  function rebuyQtyIntentFromEvent(event) {
    if (!event || !event.target) return null;
    var info = rebuyQtyIntent(event.target);
    if (info) return info;
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return null;
    var row = target.closest('.rebuy-cart__flyout-item, .rebuy-cart__item');
    if (!row) return null;
    if (row.classList && row.classList.contains('is-free-gift')) return null;
    if (target.closest('.rebuy-cart__flyout-item-remove, [aria-label*="emove"]')) return null;
    var widget =
      target.closest('.rebuy-cart__flyout-item-quantity-widget') ||
      target.closest('.rebuy-cart__flyout-item-quantity') ||
      target.closest('[class*="quantity-widget"]');
    if (!widget || typeof widget.getBoundingClientRect !== 'function') return null;
    var label = widget.querySelector
      ? widget.querySelector('.rebuy-cart__flyout-item-quantity-widget-label, input')
      : null;
    if (label && (target === label || (label.contains && label.contains(target)))) return null;
    var rect = widget.getBoundingClientRect();
    var x = event.clientX;
    if (x == null && event.changedTouches && event.changedTouches[0]) {
      x = event.changedTouches[0].clientX;
    }
    if (x == null && event.touches && event.touches[0]) x = event.touches[0].clientX;
    if (x == null) return { row: row, delta: 1 };
    return { row: row, delta: x >= rect.left + rect.width * 0.5 ? 1 : -1 };
  }

  function isCartAddUrl(url) {
    return /\/cart\/add\.js(\?|$)/i.test(String(url || ''));
  }

  /** After we handle a +/- tap, Rebuy's own add.js must not create a second line. */
  function shouldSwallowRebuyAdd(url, deliveredAt, now, windowMs) {
    if (!isCartAddUrl(url)) return false;
    var win = windowMs == null ? 1500 : windowMs;
    if (!deliveredAt) return false;
    var t = now == null ? 0 : now;
    return t - deliveredAt >= 0 && t - deliveredAt < win;
  }

  function selectedIntervalValue(selectEl) {
    if (!selectEl) return '';
    var opts = selectEl.options;
    if (opts && opts.length) {
      var idx = selectEl.selectedIndex;
      if (idx >= 0 && opts[idx]) return String(opts[idx].value || '');
    }
    return selectEl.value ? String(selectEl.value) : '';
  }

  function shouldFallbackAddItem(cartMutated) {
    return !cartMutated;
  }

  return {
    isGiftItem: isGiftItem,
    isSub: isSub,
    linePlanId: linePlanId,
    planSignature: planSignature,
    lineMergeSignature: lineMergeSignature,
    discountAllocKey: discountAllocKey,
    lineDedupeSignature: lineDedupeSignature,
    familyItems: familyItems,
    familyQuantity: familyQuantity,
    familyQtyWrite: familyQtyWrite,
    trialUnitsFor: trialUnitsFor,
    expectedTrial: expectedTrial,
    eligibleCents: eligibleCents,
    findExistingSubscribeLine: findExistingSubscribeLine,
    shouldHaveBottle: shouldHaveBottle,
    shouldAddBottle: shouldAddBottle,
    shouldClearBottleOptOut: shouldClearBottleOptOut,
    rowLineKey: rowLineKey,
    looksLikeLineKey: looksLikeLineKey,
    isConfiguredGiftHandle: isConfiguredGiftHandle,
    handleFromClassName: handleFromClassName,
    parentItemForQtyRow: parentItemForQtyRow,
    giftMetaForRow: giftMetaForRow,
    giftMetaByVariant: giftMetaByVariant,
    rebuyQtyIntent: rebuyQtyIntent,
    rebuyQtyIntentFromEvent: rebuyQtyIntentFromEvent,
    isCartAddUrl: isCartAddUrl,
    shouldSwallowRebuyAdd: shouldSwallowRebuyAdd,
    planNameMatchesItem: planNameMatchesItem,
    cadenceToken: cadenceToken,
    scheduleWaitMs: scheduleWaitMs,
    itemByLineKey: itemByLineKey,
    qtyJobCanUseCachedCart: qtyJobCanUseCachedCart,
    qtyTargetAfterDelta: qtyTargetAfterDelta,
    shouldDiscardGiftSync: shouldDiscardGiftSync,
    giftSyncBlockedByQty: giftSyncBlockedByQty,
    qtyGiftSyncWaitMs: qtyGiftSyncWaitMs,
    mergeCollapseWrites: mergeCollapseWrites,
    mergeFoldWrites: mergeFoldWrites,
    selectedIntervalValue: selectedIntervalValue,
    shouldFallbackAddItem: shouldFallbackAddItem,
    pickOtpIfUnambiguous: pickOtpIfUnambiguous,
    isSameQtyTarget: isSameQtyTarget,
    coalesceQtyJob: coalesceQtyJob
  };
});
